import { useState } from "react";
import { useDispatch } from "react-redux";
import { onCreateOrderPaypal, onSendPaidMessage } from "../store/MatchThunk";
import User from "../../../profile/domain/entities/getUser";
import { AppDispatch } from "../../../../core/store/store";

interface UseDirectMessageActionsParams {
	user: User | undefined;
	text: string;
	setText: (text: string) => void;
	setInputValue: (value: number) => void;
	availableMessageCount: number;
	onMessageSent: () => Promise<void>;
	handleUpdateMatchesSearch?: (currentUserId: string) => void;
    setHasCredits: React.Dispatch<React.SetStateAction<boolean>>;
    setIsSendMessageDirect: React.Dispatch<React.SetStateAction<boolean>>;
}

const useDirectMessageActions = ({ 
	user,
	text,
	setText,
	setInputValue,
	availableMessageCount,
	onMessageSent,
	handleUpdateMatchesSearch,
	setHasCredits,
	setIsSendMessageDirect }: UseDirectMessageActionsParams) => {
	const dispatch: AppDispatch = useDispatch();
	const [isLoadingPayMessage, setIsLoadingPayMessage] = useState<boolean>(false);
	const [errorSendMessageDirect, setErrorSendMessageDirect] = useState<boolean>(false);
	const [directMessageError, setDirectMessageError] = useState<string>("");
	const [errorNumberMessage, setErrorNumberMessage] = useState<string>("");

	const handleDirectMessage = () => {
		setHasCredits(availableMessageCount === 0);
	};

	const handleSendDirectMessage = async (currentUserId: string): Promise<boolean> => {
		if (!user) return false;
		setIsLoadingPayMessage(true);
		setDirectMessageError("");
		const response = await dispatch(
			onSendPaidMessage({
				user_id: currentUserId,
				message: text.trim(),
			})
		);

		if (response.meta?.requestStatus === "fulfilled") {
			await onMessageSent();
			setIsLoadingPayMessage(false);
			setIsSendMessageDirect(false);
			setText("");
			if (handleUpdateMatchesSearch) handleUpdateMatchesSearch(currentUserId);
			return true;
		}

		const errorMessage =
			"error" in response && response.error?.message
				? response.error.message
				: "Something went wrong. Please try again later.";
		setDirectMessageError(errorMessage);
		setIsLoadingPayMessage(false);
		return false;
	};

	const handlePayForMessage = async (productId: string, quantity: number = 1) => {
		setIsLoadingPayMessage(true);
		if (quantity === 0 || quantity > 10) {
			setErrorNumberMessage("Please enter a number between 1 and 10 for payment messages");
			setIsLoadingPayMessage(false);
			return;
		}
		setErrorNumberMessage("");
		const result = await dispatch(
			onCreateOrderPaypal({
				product_id: productId,
				quantity: quantity,
			})
		);
		const url: string = result.payload as string;
		if (url) {
			setIsLoadingPayMessage(false);
			window.open(url, "_blank");
			setHasCredits(false);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(e.target.value);
		setInputValue(value);
		switch (true) {
			case value === 0:
				setErrorNumberMessage("The minimum quantity can be 1");
				break;
			case value > 10:
				setErrorNumberMessage("The quantity cannot be greater than 10");
				break;
			default:
				setErrorNumberMessage("");
				break;
		}
	};

	const handleSendMessage = () => {
		if (text) {
			setIsSendMessageDirect(true);
			setErrorSendMessageDirect(false);
			setDirectMessageError("");
		} else {
			setErrorSendMessageDirect(true);
		}
	};

	const handleKeyDownTextarea = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			if (text.trim()) {
				setIsSendMessageDirect(true);
			}
		}
	};

	return {
		isLoadingPayMessage,
		errorSendMessageDirect,
		directMessageError,
		errorNumberMessage,
		handleDirectMessage,
		handleSendDirectMessage,
		handlePayForMessage,
		handleInputChange,
		handleSendMessage,
		handleKeyDownTextarea,
	};
};

export default useDirectMessageActions;
